// src/components/RegionMain.jsx
import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';

import {
  getZones,
  getCircles,
  getClusters,
  getClinics
} from '../../Services/Api';

export default function RegionMain({ form, handleChange }) {
  const [zoneOptions, setZoneOptions]       = useState([]);
  const [circleOptions, setCircleOptions]   = useState([]);
  const [clusterOptions, setClusterOptions] = useState([]);
  const [clinicOptions, setClinicOptions]   = useState([]);

  // Fetch zones on mount
  useEffect(() => {
    getZones()
      .then(res => {
        if (res.data?.status === 'OK') {
          setZoneOptions(
            res.data.data.map(z => ({ label: z.zoneName, value: z.zoneCode }))
          );
        }
      })
      .catch(console.error);
  }, []);

  // Zone → circles
  const handleZoneChange = ({ value }) => {
    handleChange('regions.zones', value ? [value] : []);
    handleChange('regions.circles', []);
    handleChange('regions.clusters', []);
    handleChange('regions.clinics', []);
    setCircleOptions([]);
    setClusterOptions([]);
    setClinicOptions([]);

    if (value) {
      getCircles(value)
        .then(res => {
          if (res.data?.status === 'OK') {
            setCircleOptions(
              res.data.data.map(c => ({ label: c.circleName, value: c.circleCode }))
            );
          }
        })
        .catch(err => {
          console.error('Failed to fetch circles:', err);
          setCircleOptions([]);
        });
    }
  };

  // Circles (multi) → clusters
  const handleCircleChange = ({ value }) => {
    handleChange('regions.circles', value);
    handleChange('regions.clusters', []);
    handleChange('regions.clinics', []);
    setClusterOptions([]);
    setClinicOptions([]);

    if (value.length) {
      getClusters(value)
        .then(res => {
          if (res.data?.status === 'OK') {
            setClusterOptions(
              res.data.data.map(cl => ({ label: cl.clusterName, value: cl.clusterCode }))
            );
          }
        })
        .catch(err => {
          console.error('Failed to fetch clusters:', err);
          setClusterOptions([]);
        });
    }
  };

  // Clusters (multi) → clinics
  const handleClusterChange = ({ value }) => {
    handleChange('regions.clusters', value);
    handleChange('regions.clinics', []);
    setClinicOptions([]);

    if (value.length) {
      getClinics(value)
        .then(res => {
          if (res.data?.status === 'OK') {
            setClinicOptions(
              res.data.data.map(clinic => ({
                label: clinic.ecliniceName,
                value: clinic.hospitalCode
              }))
            );
          }
        })
        .catch(err => {
          console.error('Failed to fetch clinics:', err);
          setClinicOptions([]);
        });
    }
  };

  // Clinics multi-select
  const handleClinicChange = ({ value }) =>
    handleChange('regions.clinics', value);

  const clearAll = () => {
    handleChange('regions.zones', []);
    handleChange('regions.circles', []);
    handleChange('regions.clusters', []);
    handleChange('regions.clinics', []);
    setCircleOptions([]);
    setClusterOptions([]);
    setClinicOptions([]);
  };

  const selectedCount = () => {
    const r = form.regions || {};
    return (
      (r.zones?.length   || 0) +
      (r.circles?.length || 0) +
      (r.clusters?.length|| 0) +
      (r.clinics?.length || 0)
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-xl font-semibold">Region Selection</h4>
        {selectedCount() > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedCount()} selected
            </span>
            <Button
              label="Clear All"
              onClick={clearAll}
              className="p-button-sm p-button-outlined p-button-secondary"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Zone (single) */}
        <div className="flex flex-col">
          <label className="mb-2 font-medium text-gray-700">
            Select Zone <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={form?.regions?.zones?.[0] || ''}
            options={zoneOptions}
            onChange={handleZoneChange}
            placeholder="Select Zone"
            className="w-full border border-gray-300 rounded p-0"
          />
        </div>

        {/* Circles (multi) */}
        {form?.regions?.zones?.length > 0 && (
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">
              Select Circles <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              value={form.regions.circles || []}
              options={circleOptions}
              onChange={handleCircleChange}
              placeholder="Select Circles"
              display="chip"
              className="w-full"
            />
          </div>
        )}

        {/* Clusters (multi) */}
        {form?.regions?.circles?.length > 0 && (
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">
              Select Clusters <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              value={form.regions.clusters || []}
              options={clusterOptions}
              onChange={handleClusterChange}
              placeholder="Select Clusters"
              display="chip"
              className="w-full"
            />
          </div>
        )}

        {/* Clinics (multi) */}
        {form?.regions?.clusters?.length > 0 && (
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">
              Select Clinics <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              value={form.regions.clinics || []}
              options={clinicOptions}
              onChange={handleClinicChange}
              placeholder="Select Clinics"
              display="chip"
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Summary */}
      {selectedCount() > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h5 className="font-medium text-gray-700 mb-2">
            Selection Summary:
          </h5>
          <div className="text-sm text-gray-600 space-y-1">
            {form.regions.zones?.length > 0 && (
              <p><strong>Zone:</strong> {form.regions.zones.join(', ')}</p>
            )}
            {form.regions.circles?.length > 0 && (
              <p><strong>Circles:</strong> {form.regions.circles.join(', ')}</p>
            )}
            {form.regions.clusters?.length > 0 && (
              <p><strong>Clusters:</strong> {form.regions.clusters.join(', ')}</p>
            )}
            {form.regions.clinics?.length > 0 && (
              <p><strong>Clinics:</strong> {form.regions.clinics.join(', ')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
